import React, { useState, useEffect } from 'react';
import { RefreshCw, Pencil, X } from 'lucide-react';
import { dashboardApi } from '../api/dashboardApi';
import './LicenseLimitsPage.css';

interface License {
  id: string;
  email: string;
  org_name?: string;
  status: string;
  fingerprint: string;
  days_remaining: number;
  limits?: {
    max_cameras: number;
    entry_cameras: number;
    exit_cameras: number;
    max_members: number;
    platform?: string;
    extension?: string;
    show_record_images?: boolean;
    enable_data_deletion?: boolean;
    enable_password_change?: boolean;
    show_dashboard_auth_tabs?: boolean;
    demography?: 'hidden' | 'gender_only' | 'both';
    updated_at?: string;
    updated_by?: string;
  };
}

interface EditModalState {
  visible: boolean;
  license: License | null;
  entry_cameras: number;
  exit_cameras: number;
  max_members: number;
  platform: string;
  extension: string;
  show_record_images: boolean;
  enable_data_deletion: boolean;
  enable_password_change: boolean;
  show_dashboard_auth_tabs: boolean;
  demography: 'hidden' | 'gender_only' | 'both';
  loading: boolean;
}

const EXTENSION_LABELS: Record<string, string> = {
  '': 'Core only',
  lathe_monitor: 'Lathe Monitor',
  gymdesk: 'Gymdesk',
};

const PLATFORM_LABELS: Record<string, string> = {
  none: 'Settings',
  attendance: 'Office',
  retail: 'Retail',
  'attendance+retail': 'Both',
  monitoring: 'Monitoring',
};

const DEMO_LABELS: Record<string, string> = {
  hidden: 'Hidden',
  gender_only: 'Gender',
  both: 'Age & gender',
};

const displayOrg = (org?: string) => org?.trim() || '—';

const LicenseLimitsPage: React.FC = () => {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editModal, setEditModal] = useState<EditModalState>({
    visible: false,
    license: null,
    entry_cameras: 1,
    exit_cameras: 0,
    max_members: 5,
    platform: 'attendance',
    extension: '',
    show_record_images: false,
    enable_data_deletion: false,
    enable_password_change: false,
    show_dashboard_auth_tabs: true,
    demography: 'hidden',
    loading: false,
  });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchLicenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await dashboardApi.getLicenses({ page, limit: 20 });

      const licensesWithLimits = response.licenses.map((license) => {
        const limits = (license as License).limits;
        if (limits && typeof limits.entry_cameras === 'number') {
          return { ...(license as License), limits };
        }
        return {
          ...(license as License),
          limits: {
            max_cameras: 1,
            entry_cameras: 1,
            exit_cameras: 0,
            max_members: 5,
            show_record_images: false,
            enable_data_deletion: false,
            enable_password_change: false,
            show_dashboard_auth_tabs: true,
            demography: 'hidden' as const,
          },
        };
      });

      setLicenses(licensesWithLimits);
      setTotalPages(response.pagination.total_pages);
    } catch (err) {
      setError('Failed to fetch licenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (license: License) => {
    setEditModal({
      visible: true,
      license,
      entry_cameras: license.limits?.entry_cameras ?? 1,
      exit_cameras: license.limits?.exit_cameras ?? 0,
      max_members: license.limits?.max_members ?? 5,
      platform: license.limits?.platform || 'attendance',
      extension: license.limits?.extension || '',
      show_record_images: license.limits?.show_record_images === true,
      enable_data_deletion: license.limits?.enable_data_deletion === true,
      enable_password_change: license.limits?.enable_password_change === true,
      show_dashboard_auth_tabs: license.limits?.show_dashboard_auth_tabs !== false,
      demography: license.limits?.demography || 'hidden',
      loading: false,
    });
  };

  const closeEditModal = () => {
    setEditModal({
      visible: false,
      license: null,
      entry_cameras: 1,
      exit_cameras: 0,
      max_members: 5,
      platform: 'attendance',
      extension: '',
      show_record_images: false,
      enable_data_deletion: false,
      enable_password_change: false,
      show_dashboard_auth_tabs: true,
      demography: 'hidden',
      loading: false,
    });
  };

  const handleSaveLimits = async () => {
    if (!editModal.license) return;
    setEditModal((prev) => ({ ...prev, loading: true }));

    try {
      await dashboardApi.updateLicenseLimits(editModal.license.id, {
        entry_cameras: editModal.entry_cameras,
        exit_cameras: editModal.exit_cameras,
        max_members: editModal.max_members,
        platform: editModal.platform,
        extension: editModal.extension,
        show_record_images: editModal.show_record_images,
        enable_data_deletion: editModal.enable_data_deletion,
        enable_password_change: editModal.enable_password_change,
        show_dashboard_auth_tabs: editModal.show_dashboard_auth_tabs,
        demography: editModal.demography,
      });

      setSuccessMessage(`Updated ${displayOrg(editModal.license.org_name)}`);
      setTimeout(() => setSuccessMessage(null), 3000);
      closeEditModal();
      fetchLicenses();
    } catch (err) {
      console.error('Failed to update limits:', err);
      setError('Failed to update limits');
      setEditModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const platformLabel = (platform?: string) =>
    PLATFORM_LABELS[platform || 'attendance'] || platform || 'Office';

  const extensionLabel = (extension?: string) =>
    EXTENSION_LABELS[extension || ''] || extension || 'Core only';

  const uiSummary = (license: License) => {
    const images = license.limits?.show_record_images ? 'Images on' : 'Images off';
    const deletion = license.limits?.enable_data_deletion ? 'Deletion on' : 'Deletion off';
    const pwd = license.limits?.enable_password_change ? 'Pwd change on' : 'Pwd change off';
    const tabs = license.limits?.show_dashboard_auth_tabs !== false ? 'Auth tabs on' : 'Auth tabs off';
    const demo = DEMO_LABELS[license.limits?.demography || 'hidden'] || 'Hidden';
    return `${images} · ${deletion} · ${pwd} · ${tabs} · ${demo}`;
  };

  if (loading && licenses.length === 0) {
    return <div className="ll-page ll-page-loading">Loading limits…</div>;
  }

  return (
    <div className="ll-page">
      <div className="ll-toolbar">
        <span className="ll-toolbar-note">Defaults: 1 entry · 0 exit · 5 members · images off</span>
        <button type="button" className="ll-btn-refresh" onClick={fetchLicenses}>
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {successMessage && (
        <div className="ll-toast ll-toast--ok">
          <span>{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage(null)} aria-label="Dismiss">×</button>
        </div>
      )}
      {error && (
        <div className="ll-toast ll-toast--err">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="ll-table-wrap">
        {loading && licenses.length > 0 && (
          <div className="ll-loading">
            <RefreshCw size={20} className="animate-spin" color="#64748b" />
          </div>
        )}
        <div className="ll-table-scroll">
          {licenses.length === 0 ? (
            <div className="ll-empty">No licenses found.</div>
          ) : (
            <table className="ll-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Platform</th>
                  <th>Extension</th>
                  <th>Cameras / Members</th>
                  <th>Client UI</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {licenses.map((license) => (
                  <tr key={license.id} onClick={() => openEditModal(license)}>
                    <td title={`${displayOrg(license.org_name)} · ${license.email}`}>
                      <span className="ll-org-name">{displayOrg(license.org_name)}</span>
                      <span className="ll-org-email">{license.email}</span>
                    </td>
                    <td>
                      <span className="ll-text-muted">{platformLabel(license.limits?.platform)}</span>
                    </td>
                    <td>
                      <span className="ll-text-muted">{extensionLabel(license.limits?.extension)}</span>
                    </td>
                    <td>
                      <span className="ll-limits">
                        <strong>{license.limits?.entry_cameras ?? 1}</strong> in ·{' '}
                        <strong>{license.limits?.exit_cameras ?? 0}</strong> out ·{' '}
                        <strong>{license.limits?.max_members ?? 5}</strong> members
                      </span>
                    </td>
                    <td>
                      <span className="ll-ui-summary">{uiSummary(license)}</span>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className="ll-icon-edit"
                        title="Edit limits"
                        onClick={() => openEditModal(license)}
                      >
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="ll-pagination">
        <span>Page {page} of {totalPages}</span>
        <div className="ll-pagination-btns">
          <button type="button" className="ll-page-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Prev
          </button>
          <button type="button" className="ll-page-btn" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
            Next
          </button>
        </div>
      </div>

      {editModal.visible && (
        <div className="ll-overlay" onClick={closeEditModal}>
          <div className="ll-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ll-modal-header">
              <div style={{ minWidth: 0, flex: 1 }}>
                <h2 className="ll-modal-title">Edit limits</h2>
                <p className="ll-modal-email" title={editModal.license?.email}>
                  {displayOrg(editModal.license?.org_name)} · {editModal.license?.email}
                </p>
              </div>
              <button type="button" className="ll-modal-close" onClick={closeEditModal} aria-label="Close">
                <X size={16} />
              </button>
            </div>

            <div className="ll-modal-body">
              <div className="ll-field">
                <label htmlFor="ll-platform">Platform</label>
                <select
                  id="ll-platform"
                  value={editModal.platform}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, platform: e.target.value }))}
                >
                  <option value="none">Settings only</option>
                  <option value="attendance">Office tracking</option>
                  <option value="retail">Retail analytics</option>
                  <option value="attendance+retail">Both modes</option>
                  <option value="monitoring">Monitoring</option>
                </select>
              </div>

              <div className="ll-field">
                <label htmlFor="ll-extension">Extension</label>
                <select
                  id="ll-extension"
                  value={editModal.extension}
                  onChange={(e) => setEditModal((prev) => ({ ...prev, extension: e.target.value }))}
                >
                  <option value="">Core only (no extension)</option>
                  <option value="lathe_monitor">Lathe Monitor — Equipment Monitor page</option>
                  <option value="gymdesk">Gymdesk — memberships, receipts, WhatsApp</option>
                </select>
              </div>

              <div className="ll-num-row">
                <div className="ll-field" style={{ marginBottom: 0 }}>
                  <label htmlFor="ll-entry">Entry</label>
                  <input
                    id="ll-entry"
                    type="number"
                    min={0}
                    max={100}
                    value={editModal.entry_cameras}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, entry_cameras: parseInt(e.target.value, 10) || 0 }))
                    }
                  />
                </div>
                <div className="ll-field" style={{ marginBottom: 0 }}>
                  <label htmlFor="ll-exit">Exit</label>
                  <input
                    id="ll-exit"
                    type="number"
                    min={0}
                    max={100}
                    value={editModal.exit_cameras}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, exit_cameras: parseInt(e.target.value, 10) || 0 }))
                    }
                  />
                </div>
                <div className="ll-field" style={{ marginBottom: 0 }}>
                  <label htmlFor="ll-members">Members</label>
                  <input
                    id="ll-members"
                    type="number"
                    min={1}
                    max={10000}
                    value={editModal.max_members}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, max_members: parseInt(e.target.value, 10) || 5 }))
                    }
                  />
                </div>
              </div>

              <div className="ll-toggle-row">
                <div>
                  <span className="ll-toggle-label">Record images</span>
                  <span className="ll-toggle-hint">Show on client dashboard</span>
                </div>
                <label className="ll-switch">
                  <input
                    type="checkbox"
                    checked={editModal.show_record_images}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, show_record_images: e.target.checked }))
                    }
                  />
                  <span className="ll-switch-slider" />
                </label>
              </div>

              <div className="ll-toggle-row">
                <div>
                  <span className="ll-toggle-label">Data deletion</span>
                  <span className="ll-toggle-hint">Show gallery clear in client Settings</span>
                </div>
                <label className="ll-switch">
                  <input
                    type="checkbox"
                    checked={editModal.enable_data_deletion}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, enable_data_deletion: e.target.checked }))
                    }
                  />
                  <span className="ll-switch-slider" />
                </label>
              </div>

              <div className="ll-toggle-row">
                <div>
                  <span className="ll-toggle-label">Password change</span>
                  <span className="ll-toggle-hint">Allow change password on client login (encrypted)</span>
                </div>
                <label className="ll-switch">
                  <input
                    type="checkbox"
                    checked={editModal.enable_password_change}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, enable_password_change: e.target.checked }))
                    }
                  />
                  <span className="ll-switch-slider" />
                </label>
              </div>

              <div className="ll-toggle-row">
                <div>
                  <span className="ll-toggle-label">Dashboard auth tabs</span>
                  <span className="ll-toggle-hint">Show Visitors / Employees / All on client dashboard</span>
                </div>
                <label className="ll-switch">
                  <input
                    type="checkbox"
                    checked={editModal.show_dashboard_auth_tabs}
                    onChange={(e) =>
                      setEditModal((prev) => ({ ...prev, show_dashboard_auth_tabs: e.target.checked }))
                    }
                  />
                  <span className="ll-switch-slider" />
                </label>
              </div>

              <div className="ll-field">
                <label htmlFor="ll-demography">Demography</label>
                <select
                  id="ll-demography"
                  value={editModal.demography}
                  onChange={(e) =>
                    setEditModal((prev) => ({
                      ...prev,
                      demography: e.target.value as EditModalState['demography'],
                    }))
                  }
                >
                  <option value="hidden">Hidden</option>
                  <option value="gender_only">Gender only</option>
                  <option value="both">Age & gender</option>
                </select>
              </div>
            </div>

            <div className="ll-modal-footer">
              <button type="button" className="ll-btn-cancel" onClick={closeEditModal}>
                Cancel
              </button>
              <button type="button" className="ll-btn-save" onClick={handleSaveLimits} disabled={editModal.loading}>
                {editModal.loading ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LicenseLimitsPage;
